import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import Snapshot3D from './3DModel'
import { formatMacAddress } from '../../utils/string'
import type { PairedRuuviTag, RuuviTagAcceleration, RuuviTag } from '../../types'
import useDialog from '../../hooks/useDialog'
import { LoadingButton } from '@mui/lab'
import { getRuuviTagAccelerationNow, getRuuviTagConditionNow } from '../../services/ruuviTagService'
import { useDispatch } from 'react-redux'
import { createErrorNotification } from '../../redux/slices/notifications'
import { DeviceType } from '../../enums'
import RuuviTagAssignmentNotices from './RuuviTagAssignmentNotices'
import RuuviTagSignalChip from '../RuuviTagSignalChip'

export interface RuuviTagScannedListItemButtonProps {
  tag: PairedRuuviTag
  onSelect: (tag: RuuviTag) => void
  isFetching?: boolean
  onExitInstallation: () => void
  onTagUpdate?: (tag: PairedRuuviTag) => void
}

function computeRotationDegFromTag(tag: PairedRuuviTag) {
  const accel: RuuviTagAcceleration | undefined = tag.latest?.acceleration
  const rawX = accel?.xAvg ?? accel?.xMax ?? 0
  const rawY = accel?.yAvg ?? accel?.yMax ?? 0
  const rawZ = accel?.zAvg ?? accel?.zMax ?? 0

  const toNumber = (value: unknown) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const x = toNumber(rawX)
  const y = toNumber(rawY)
  const z = toNumber(rawZ)

  const magnitudeSq = x * x + y * y + z * z
  if (!Number.isFinite(magnitudeSq) || magnitudeSq < 1e-4) {
    return { x: -90, y: 0, z: 0 }
  }

  const magnitude = Math.sqrt(magnitudeSq)
  const xyMagnitude = Math.sqrt(x * x + y * y)

  const wrapDeg = (deg: number) => ((((deg + 180) % 360) + 360) % 360) - 180
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

  const tiltRatio = clamp(z / (magnitude || 1), -1, 1)
  let rotX = -((Math.asin(tiltRatio) * 180) / Math.PI)
  if (Math.abs(tiltRatio) > 0.98) {
    rotX = tiltRatio >= 0 ? -90 : 90
  } else if (Math.abs(z) < 120) {
    rotX = 0
  }

  let rotY = 0

  const yawOffsetDeg = 26
  let rotZ = wrapDeg((-Math.atan2(y, x) * 180) / Math.PI - yawOffsetDeg)

  if (xyMagnitude < 120 && Math.abs(z) > 400) {
    rotZ = z >= 0 ? -180 : 0
  }

  if (Math.abs(Math.abs(rotZ) - 180) <= 1.5) {
    rotZ = rotZ < 0 ? -180 : 180
  }

  rotX = wrapDeg(rotX)
  rotY = wrapDeg(rotY)
  rotZ = wrapDeg(rotZ)

  return {
    x: rotX,
    y: rotY,
    z: rotZ
  }
}

export default function RuuviTagScannedListItemButton({
  tag: initialTag,
  onSelect,
  isFetching: _isFetching,
  onExitInstallation,
  onTagUpdate
}: RuuviTagScannedListItemButtonProps) {
  const { t } = useTranslation('installation')
  const theme = useTheme()
  const { open: fullscreenOpen, openDialog, closeDialog } = useDialog()
  const dispatch = useDispatch()
  const [tag, setTag] = useState<PairedRuuviTag>(initialTag)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const asset = tag.asset ?? undefined
  const assetTagDevice =
    asset?.ruuviTag?.id === tag.id ? (asset.ruuviTag.device ?? undefined) : undefined
  const hostDevice = tag.device ?? assetTagDevice ?? undefined
  const valveDevice = hostDevice?.type === DeviceType.VALVE ? hostDevice : undefined
  const projectId = asset?.projectId ?? null
  const isValveManaged = Boolean(valveDevice)
  const isAssignedToAnyProject = projectId !== null
  const canSelectTag = !isValveManaged && !isAssignedToAnyProject
  const tagRef = useRef(tag)

  useEffect(() => {
    tagRef.current = tag
  }, [tag])

  useEffect(() => {
    setTag((prev) => {
      if (prev === undefined || prev === null) return initialTag

      const incomingTimestamp = Number(new Date(initialTag.updatedAt).getTime())
      const currentTimestamp = Number(new Date(prev.updatedAt).getTime())

      if (Number.isNaN(incomingTimestamp) || incomingTimestamp >= currentTimestamp) {
        return initialTag
      }

      // Keep the newer local state if the incoming data is stale.
      return prev
    })
  }, [initialTag])

  const accel = tag.latest?.acceleration
  const x = accel?.xAvg ?? accel?.xMax ?? '—'
  const y = accel?.yAvg ?? accel?.yMax ?? '—'
  const z = accel?.zAvg ?? accel?.zMax ?? '—'
  const rssi = tag.latest?.condition?.rssi ?? accel?.rssi ?? '—'
  const fmt = (value: unknown) => (typeof value === 'number' ? value.toString() : value)

  const handleOpenDialog = (event?: React.MouseEvent) => {
    event?.stopPropagation()
    openDialog()
  }

  const handleSelectTag = () => {
    onSelect(tag)
    if (canSelectTag) {
      closeDialog()
    }
  }

  const handleExitInstallation = () => {
    closeDialog()
    onExitInstallation()
  }

  const handleRefreshOrientation = async () => {
    if (isRefreshing) return
    const tagId = tag.id
    setIsRefreshing(true)
    try {
      const [conditionResult, accelerationResult] = await Promise.allSettled([
        getRuuviTagConditionNow(tagId),
        getRuuviTagAccelerationNow(tagId)
      ])

      setTag((prev) => ({
        ...prev,
        latest: {
          ...prev?.latest,
          condition:
            conditionResult.status === 'fulfilled' ? conditionResult.value : prev.latest?.condition,
          acceleration:
            accelerationResult.status === 'fulfilled'
              ? accelerationResult.value
              : prev.latest?.acceleration
        }
      }))
    } catch (_error) {
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setIsRefreshing(false)
    }
  }

  const secondaryText = (() => {
    if (!asset) {
      return t('unknown', { defaultValue: 'Unknown' })
    }
    if (asset.projectId == null) {
      return t('notInstalled', { defaultValue: 'Not installed' })
    }
    const parts: string[] = []
    const levelName = asset.level?.name
    const liftName = asset.lift?.name
    if (levelName) parts.push(levelName)
    if (liftName) parts.push(liftName)
    if (parts.length > 0) return parts.join(' / ')
    return asset.name || t('installed', { defaultValue: 'Installed' })
  })()

  const handleDetachSuccess = useCallback(
    (updated: PairedRuuviTag) => {
      const base = tagRef.current
      const nextTag: PairedRuuviTag = {
        ...(base ?? updated),
        ...(updated as PairedRuuviTag),
        latest: (updated as PairedRuuviTag).latest ?? base?.latest
      }

      setTag(nextTag)
      onTagUpdate?.(nextTag)
    },
    [onTagUpdate]
  )

  return (
    <>
      <ListItemButton
        onClick={handleSelectTag}
        aria-disabled={!canSelectTag}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' },
          alignItems: 'center',
          gap: 1,
          py: 1.25,
          px: 1.5,
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'light' ? '#f7f9fc' : '#1b2635',
          cursor: 'pointer',
          opacity: canSelectTag ? 1 : 0.65,
          ...(canSelectTag
            ? {
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? '#eef3fb' : '#223041'
                }
              }
            : {
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? '#f7f9fc' : '#1b2635'
                }
              })
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            width: '100%'
          }}
        >
          <ListItemText
            sx={{ m: 0 }}
            primary={formatMacAddress(tag.id)}
            secondary={secondaryText}
            primaryTypographyProps={{ sx: { order: '0 !important' } }}
            secondaryTypographyProps={{ sx: { order: '1 !important' } }}
          />

          <RuuviTagAssignmentNotices
            tag={tag}
            valveDevice={valveDevice}
            projectId={projectId}
            onDetachSuccess={handleDetachSuccess}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="body2" component="div">
              {fmt(x)} / {fmt(y)} / {fmt(z)}
            </Typography>
            {tag.latest?.condition && (
              <Typography variant="body2" component="div">
                {tag.latest.condition.temperatureAvg.toFixed(1)}°C
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              RSSI: {fmt(rssi)}
            </Typography>
          </Box>

          <Box
            component="div"
            role="button"
            tabIndex={0}
            aria-label={String(t('openFullScreen'))}
            onClick={handleOpenDialog}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                handleOpenDialog()
              }
            }}
            sx={{
              width: { xs: '95%', sm: 120 },
              minWidth: { sm: 120 },
              aspectRatio: '1 / 1',
              height: { xs: 'auto', sm: 80 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.mode === 'light' ? '#f7f9fc' : '#1b2635',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              outline: 'none',
              '&:focus-visible': { boxShadow: '0 0 0 2px rgba(255,255,255,0.4)' }
            }}
          >
            <Snapshot3D
              modelUrl={'/static/ruuvitag-pro-simplified-enclosure.glb'}
              rotationDeg={computeRotationDegFromTag(tag)}
              background={theme.palette.mode === 'light' ? 0xf7f9fc : 0x1b2635}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>

          <IconButton
            size="medium"
            onClick={handleOpenDialog}
            aria-label={t('openFullScreen')}
            sx={{ width: 36, height: 36, mt: { xs: 1, sm: 0 } }}
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Box>
      </ListItemButton>

      <Dialog fullScreen open={fullscreenOpen} onClose={closeDialog}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6">{formatMacAddress(tag.id)}</Typography>

            <RuuviTagSignalChip
              rssi={tag.latest?.condition?.rssi ?? tag.latest?.acceleration?.rssi ?? null}
            />

            <IconButton aria-label={t('close')} onClick={closeDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            p: { xs: 2, sm: 3 },
            maxHeight: '100vh',
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              width: { xs: '95vw', sm: '90vw', md: '80vw' },
              height: { xs: '60vh', sm: '70vh', md: '80vh' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Snapshot3D
              modelUrl={'/static/ruuvitag-pro-simplified-enclosure.glb'}
              rotationDeg={computeRotationDegFromTag(tag)}
              interactive={true}
              background={theme.palette.mode === 'light' ? 0xf7f9fc : 0x1b2635}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSelectTag}
              aria-disabled={!canSelectTag}
              sx={{ opacity: canSelectTag ? 1 : 0.7 }}
            >
              {t('selectThisTag')}
            </Button>
            <LoadingButton
              variant="outlined"
              color="inherit"
              size="large"
              onClick={handleRefreshOrientation}
              loading={isRefreshing}
              disabled={isRefreshing}
            >
              {t('refreshOrientation')}
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
